import React, { useEffect, useRef, useState, useMemo } from "react";
import Globe from "react-globe.gl";
import api from "../lib/api";
import { Eye, Activity, Crown, Anchor as AnchorIcon, AlertTriangle } from "lucide-react";

/**
 * SovereignEye — 3D WebGL globe (Three.js via react-globe.gl).
 *
 * Two modes drive the same visual frame:
 *   mode="public"   → /sovereign-eye/threats — simulated cyber-threat noise
 *                     (red/orange arcs from APT hubs to US victim cities).
 *                     Mounted on Landing + GhostBridge.
 *   mode="internal" → /sovereign-eye/fleet — real EIGOO B2B leads (red),
 *                     defended hulls (gold), active strikes (cyan). Auth-gated.
 *
 * Visual posture: Luxury Nautical/Celestial — navy globe, gold defended hulls,
 * neon arcs for noise, deep-space starfield backdrop. Defaults to Hawaii on load.
 *
 * Director directive 2026.02 · master visual for "Ad Data".
 */

const HAWAII = { lat: 21.3099, lng: -157.8581, altitude: 2.4 };

export default function SovereignEye({ mode = "public", height = 520, compact = false }) {
    const globeRef = useRef(null);
    const containerRef = useRef(null);
    const [width, setWidth] = useState(800);
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    // Responsive sizing — measure parent on mount + resize.
    useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                setWidth(containerRef.current.clientWidth);
            }
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    // Pull the appropriate feed.
    useEffect(() => {
        let cancelled = false;
        const url = mode === "internal" ? "/sovereign-eye/fleet" : "/sovereign-eye/threats";
        const interval = mode === "internal" ? 30000 : 15000;
        const fetchData = async () => {
            try {
                const { data: payload } = await api.get(url);
                if (!cancelled) setData(payload);
            } catch (e) {
                if (!cancelled) {
                    console.warn("[sovereign-eye] feed fetch failed:", e?.response?.status);
                    // Internal mode auth failure → fall back to public threats so the
                    // globe still renders something rather than a blank canvas.
                    if (mode === "internal") {
                        try {
                            const { data: pub } = await api.get("/sovereign-eye/threats");
                            if (!cancelled) setData({ ...pub, _fallback: true });
                        } catch { /* noop */ }
                    }
                }
            }
        };
        fetchData();
        const t = setInterval(fetchData, interval);
        return () => { cancelled = true; clearInterval(t); };
    }, [mode]);

    // Once the globe is mounted + we have data, frame Hawaii and start the slow auto-rotate.
    useEffect(() => {
        if (!globeRef.current || !data || loaded) return;
        const view = data.default_view || HAWAII;
        try {
            globeRef.current.pointOfView(
                { lat: view.lat, lng: view.lng, altitude: view.altitude || HAWAII.altitude },
                1500
            );
            const controls = globeRef.current.controls();
            if (controls) {
                controls.autoRotate = true;
                controls.autoRotateSpeed = 0.35;
                controls.enableZoom = false;
            }
            setLoaded(true);
        } catch (e) {
            console.warn("[sovereign-eye] frame init:", e);
        }
    }, [data, loaded]);

    // --- Build globe layers from feed --------------------------------------
    const arcs = useMemo(() => {
        if (!data) return [];
        if (mode === "public" || data._fallback) {
            return (data.threats || []).map((t) => ({
                startLat: t.start.lat,
                startLng: t.start.lng,
                endLat: t.end.lat,
                endLng: t.end.lng,
                color: [t.color, "#0E2B22"],
                stroke: 0.4 + t.intensity * 0.6,
                label: t.label,
            }));
        }
        return [];
    }, [data, mode]);

    const points = useMemo(() => {
        if (!data) return [];
        if (mode === "internal" && !data._fallback) {
            const hulls = (data.hulls || []).map((h) => ({
                lat: h.lat, lng: h.lng,
                color: h.color,
                size: h.size || 0.6,
                kind: "hull",
                label: h.label,
            }));
            const leads = (data.leads || []).map((l) => ({
                lat: l.lat, lng: l.lng,
                color: l.color,
                size: l.size || 0.4,
                kind: "lead",
                label: l.label,
            }));
            return [...hulls, ...leads];
        }
        // Public mode: render hubs + targets as static reference rings.
        const hubPts = (data.hubs || []).map((h) => ({
            lat: h.lat, lng: h.lng, color: "#FF5577", size: 0.35, kind: "hub", label: h.label,
        }));
        const tgtPts = (data.targets || []).map((t) => ({
            lat: t.lat, lng: t.lng, color: "#F2C94C", size: 0.4, kind: "target", label: t.name,
        }));
        return [...hubPts, ...tgtPts];
    }, [data, mode]);

    // Anchor pulse — Hawaii always pulses gold (Sovereign Anchor — the home port).
    const ringsData = useMemo(() => ([
        { lat: HAWAII.lat, lng: HAWAII.lng, maxR: 6, propagationSpeed: 1.6, repeatPeriod: 1800 },
    ]), []);

    // Live counters
    const stats = useMemo(() => {
        if (!data) return null;
        if (mode === "internal" && !data._fallback) {
            return {
                hulls: (data.hulls || []).length,
                leads: (data.leads || []).length,
                strikes: data.strikes_count || 0,
            };
        }
        return {
            threats: (data.threats || []).length,
            hubs: (data.hubs || []).length,
            targets: (data.targets || []).length,
        };
    }, [data, mode]);

    return (
        <div
            ref={containerRef}
            data-testid={`sovereign-eye-${mode}`}
            className="relative w-full rounded-2xl overflow-hidden"
            style={{
                height,
                background: "radial-gradient(ellipse at center, hsl(220 50% 8%) 0%, hsl(220 60% 4%) 60%, #000 100%)",
            }}
        >
            {/* Star backdrop — handled by parent's starfield, but a soft luminance helps the globe pop */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 30%, hsl(40 70% 45% / 0.08), transparent 60%)" }} />

            {/* Globe */}
            <div className="absolute inset-0">
                {data && (
                    <Globe
                        ref={globeRef}
                        width={width}
                        height={height}
                        backgroundColor="rgba(0,0,0,0)"
                        showAtmosphere
                        atmosphereColor="#F2C94C"
                        atmosphereAltitude={0.18}
                        globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
                        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                        // Arcs — threat noise (public) or empty (internal)
                        arcsData={arcs}
                        arcColor="color"
                        arcDashLength={0.4}
                        arcDashGap={0.8}
                        arcDashAnimateTime={2200}
                        arcStroke="stroke"
                        arcAltitudeAutoScale={0.5}
                        arcLabel="label"
                        // Points — hulls/leads (internal) or hubs/targets (public)
                        pointsData={points}
                        pointColor="color"
                        pointAltitude={0.012}
                        pointRadius="size"
                        pointLabel="label"
                        // Rings — Hawaii anchor pulse always present
                        ringsData={ringsData}
                        ringColor={() => (t) => `rgba(242, 201, 76, ${1 - t})`}
                        ringMaxRadius="maxR"
                        ringPropagationSpeed="propagationSpeed"
                        ringRepeatPeriod="repeatPeriod"
                    />
                )}
            </div>

            {/* HUD overlay */}
            <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none z-10">
                <div className="glass rounded-lg px-3 py-2 text-xs pointer-events-auto" data-testid="sovereign-eye-title">
                    <div className="flex items-center gap-2">
                        <Eye size={14} className="text-[hsl(45_85%_68%)]" />
                        <span className="font-heading uppercase tracking-[0.3em] text-[10px] text-[hsl(45_85%_75%)]">Sovereign Eye</span>
                        <span className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full border border-[hsl(40_50%_45%/0.5)] text-muted-foreground">
                            {mode === "internal" && !data?._fallback ? "Internal · Fleet" : "Public · Noise"}
                        </span>
                    </div>
                </div>

                {stats && !compact && (
                    <div className="glass rounded-lg px-3 py-2 text-xs pointer-events-auto flex items-center gap-4" data-testid="sovereign-eye-stats">
                        {mode === "internal" && !data?._fallback ? (
                            <>
                                <Stat icon={Crown} color="#F2C94C" label="Platforms" value={stats.hulls} testid="stat-hulls" />
                                <Stat icon={AlertTriangle} color="#FF3B5C" label="Leads" value={stats.leads} testid="stat-leads" />
                                <Stat icon={Activity} color="#22D3EE" label="Deliveries" value={stats.strikes} testid="stat-strikes" />
                            </>
                        ) : (
                            <>
                                <Stat icon={Activity} color="#FF3B5C" label="Active threats" value={stats.threats} testid="stat-threats" />
                                <Stat icon={AnchorIcon} color="#F2C94C" label="Targets defended" value={stats.targets} testid="stat-targets" />
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="absolute bottom-3 right-4 text-[9px] uppercase tracking-[0.3em] text-muted-foreground pointer-events-none">
                ⚓ Anchored · Honolulu, HI
            </div>
        </div>
    );
}

function Stat({ icon: Icon, color, label, value, testid }) {
    return (
        <div className="flex items-center gap-1.5" data-testid={testid}>
            <Icon size={12} style={{ color }} />
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
            <span className="font-mono text-xs text-foreground">{value}</span>
        </div>
    );
}
